<script setup lang="ts">
import {inject, onMounted, ref} from 'vue';
import ReplacementDetectionResults from './ReplacementDetectionResults.vue';
import StatusNotFound from '../../../components/StatusNotFound.vue';
import type {SelectedReplacement} from '../../types/replacement_detection.ts';
import {NoChangesDetectedError, NotFoundError} from '../../api/errors.ts';
import {confirmReplacements, detectReplacements} from '../../api/replacement-detection.ts';
import type {RepoBasePathInfo} from '../../../types.ts';
import type {Container} from '../../../container.ts';

const props = defineProps<{
  token: string;
  repoOwner: string;
  repoName: string;
  baseRef: string;
}>();

const state = ref<'loading' | 'results' | 'confirming' | 'not-found' | 'no-changes' | 'error'>('loading');
const container = inject('container') as Container;
const appSubUrl = container.lookup('appSubUrl');
const errorMessage = ref('');
const replacements = ref<SelectedReplacement[]>([]);
const basePath: RepoBasePathInfo = {
  ownerName: props.repoOwner,
  repoName: props.repoName,
  baseRef: props.baseRef,
};

async function detect() {
  state.value = 'loading';
  try {
    const {candidates} = await detectReplacements(appSubUrl, basePath, props.token);

    // Auto-confirm if no candidates are found
    if (candidates.length === 0) {
      await confirm([]);
      return;
    }

    replacements.value = candidates.map((c) => ({checked: true, candidate: c}));
    state.value = 'results';
  } catch (e) {
    if (e instanceof NotFoundError) {
      state.value = 'not-found';
    } else {
      console.error(e);
      errorMessage.value = 'Failed to fetch detection results.';
      state.value = 'error';
    }
  }
}

async function confirm(selectedReplacements?: {source: string; dest: string}[]) {
  state.value = 'confirming';
  try {
    const filteredReplacements = selectedReplacements ?? replacements.value
      .filter((r) => r.checked)
      .map((r) => ({source: r.candidate.source, dest: r.candidate.dest}));
    const {redirect} = await confirmReplacements(appSubUrl, basePath, {token: props.token, replacements: filteredReplacements});
    window.location.replace(redirect);
  } catch (e) {
    if (e instanceof NotFoundError) {
      state.value = 'not-found';
    } else if (e instanceof NoChangesDetectedError) {
      errorMessage.value = e.message;
      state.value = 'no-changes';
    } else {
      console.error(e);
      errorMessage.value = 'Failed to confirm replacements.';
      state.value = 'error';
    }
  }
}

function goBack() {
  window.history.back();
}

function selectAll() {
  for (const r of replacements.value) r.checked = true;
}

function unselectAll() {
  for (const r of replacements.value) r.checked = false;
}

onMounted(() => {
  detect();
});
</script>

<template>
  <div class="tw:py-5">
    <!-- Loading / confirming state -->
    <div
      v-if="state === 'loading' || state === 'confirming'"
      class="loading-container"
    >
      <div class="is-loading loading-spinner" />
      <p
        v-if="state === 'loading'"
        class="loading-text"
      >
        Processing uploaded files...
      </p>
      <p
        v-if="state === 'confirming'"
        class="loading-text"
      >
        Committing files...
      </p>
    </div>

    <!-- Results state -->
    <ReplacementDetectionResults
      v-else-if="state === 'results'"
      :replacements="replacements"
      @confirm="confirm()"
      @skip="confirm([])"
      @select-all="selectAll"
      @unselect-all="unselectAll"
    />

    <!-- Not found state -->
    <StatusNotFound
      v-else-if="state === 'not-found'"
      title="Not Found"
    >
      The upload session you are trying to access does not exist or has expired.
    </StatusNotFound>

    <!-- No changes state -->
    <div
      v-else-if="state === 'no-changes'"
      class="no-changes-container"
    >
      <div class="ui negative message">
        <div class="header">
          No changes to commit
        </div>
        <p>{{ errorMessage }}</p>
      </div>
      <button
        type="button"
        class="ui button"
        @click="goBack"
      >
        Go back to upload
      </button>
    </div>

    <!-- Error state -->
    <div
      v-else-if="state === 'error'"
      class="error-container"
    >
      <div class="ui negative message">
        <p>{{ errorMessage }}</p>
      </div>
      <button
        class="ui button"
        @click="detect()"
      >
        Retry
      </button>
    </div>
  </div>
</template>

<style scoped>
@reference "#index.css";

.loading-container {
  @apply tw:text-center tw:py-10;
}

.loading-spinner {
  @apply tw:size-8 tw:mx-auto;
}

.loading-text {
  @apply tw:mt-4 tw:text-text-light-2;
}

.error-container,
.no-changes-container {
  @apply tw:text-center tw:py-5;
}
</style>
